import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token refresh interval - refresh every 7 days to keep session alive
const TOKEN_REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days in ms

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('natty_stack_token'));
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  // Refresh token to extend session
  const refreshToken = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh-token`);
      const newToken = response.data.access_token;
      localStorage.setItem('natty_stack_token', newToken);
      localStorage.setItem('natty_stack_token_refreshed', Date.now().toString());
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      
      // Check if we need to refresh token (every 7 days)
      const lastRefresh = localStorage.getItem('natty_stack_token_refreshed');
      const shouldRefresh = !lastRefresh || (Date.now() - parseInt(lastRefresh)) > TOKEN_REFRESH_INTERVAL;
      
      if (shouldRefresh) {
        refreshToken();
      }
    } else {
      setLoading(false);
    }
  }, [token, refreshToken]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setIsPremium(response.data.is_premium || false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout if it's an auth error, not a network error
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setIsPremium(response.data.is_premium || false);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  const refreshPremiumStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/premium-status`);
      setIsPremium(response.data.is_premium || false);
      if (response.data.is_premium && user) {
        setUser({ ...user, is_premium: true });
      }
      return response.data.is_premium;
    } catch (error) {
      console.error('Failed to check premium status:', error);
      return false;
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('natty_stack_token', access_token);
    localStorage.setItem('natty_stack_token_refreshed', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setIsPremium(userData.is_premium || false);
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('natty_stack_token', access_token);
    localStorage.setItem('natty_stack_token_refreshed', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setIsPremium(userData.is_premium || false);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('natty_stack_token');
    localStorage.removeItem('natty_stack_token_refreshed');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsPremium(false);
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, register, logout, 
      isAuthenticated: !!user, 
      isPremium, 
      refreshPremiumStatus,
      refreshToken,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
